// ts-check
import React, { useCallback, useContext, useMemo, useState } from 'react';
import _ from 'lodash';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useHistory } from 'react-router-dom';

import { Button, Toolbar, ToolbarContent, ToolbarGroup, ToolbarItem, Spinner, Accordion, AccordionContent, AccordionToggle, AccordionItem, Stack, StackItem } from '@patternfly/react-core';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
// import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import ModelsContainer from 'components/Model/ModelsContainer';
import ModelType from 'components/Model/ModelType';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import { useTranslation } from 'localization/useTranslation';
import { notificationsKeys, useNotification } from './queries';
import createMutationRequest from 'utils/createMutationRequest';
import useBoolean from 'utils/hooks/useBoolean';
// import styles from './AdminNotificationEditPage.module.css';
import AlertContext from 'services/AlertContext';
// import SimpleTable, { SortDirection } from 'components/Common/SimpleTable/SimpleTable';
import ConfirmModalWithOrg from 'components/Modals/ConfirmModalWithOrg';
import { privileges } from 'utils/types';
import { useAlarms } from '../Alarms/queries';
import { hasValidInput } from 'components/Model/ModelUtils';
// const defaultSortBy = {
//   path: 'displayName',
//   direction: SortDirection.DESC
// };


const defaultNotification = {
  displayName: '',
  description: '',
  broadcast: true,
  channels: {
    email: {
      active: false,
      template: '',
      subject: ''
    },
    sms: {
      active: false,
      template: ''
    }
  },
  trigger: {
    alarmTrigger: []
  }
};

export const NotificationName = ({ notificationId }) => {
  const { isLoading, notification } = useNotification(notificationId);
  return isLoading ? <BreadcrumbSkeleton /> : (notification?.displayName ?? '');
};

const AdminNotificationEditPage = ({ breadcrumbs }) => {
  const T = useTranslation();
  const api = useAPI();
  const { addErrorAlert } = useContext(AlertContext);

  const { notificationId } = useParams();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [editedNotification, setNotification] = useState(null);
  const [isOpen, showPublishDialog, hidePublishDialog] = useBoolean();

  const { notification: serverState, isLoading } = useNotification(notificationId);
  const { alarms, isLoading: isLoadingAlarms } = useAlarms();

  const alarmTriggerOptions = useMemo(() => {
    return _.map(alarms, (alarm) => ({ text: alarm.name, value: alarm.id }));
  }, [alarms]);

  // const columns = useMemo(() => {
  //   return [
  //     {
  //       label: T.common.displayname,
  //       path: 'displayName',
  //       searchItem: true,
  //       isSortable: true
  //     },
  //     {
  //       label: T.common.type,
  //       path: 'type',
  //       isSortable: true
  //     }
  //   ];
  // }, [T.common.displayname, T.common.type]);
  const model = useMemo(() => {
    return [
      {
        type: ModelType.TEXT,
        label: T.common.displayname,
        path: 'displayName',
        isRequired: true,
        hasError: _.isEmpty
      },
      {
        type: ModelType.TEXTAREA,
        label: T.common.description,
        path: 'description'
      },
      {
        type: ModelType.SWITCH,
        label: T.admin.notifications.broadcast,
        path: 'broadcast',
        onlySwitch: true
      },
      {
        type: ModelType.CHECKBOX,
        label: T.common.email,
        path: 'channels.email.active'
      },
      {
        type: ModelType.TEXT,
        label: T.common.subject,
        path: 'channels.email.subject'
      },
      {
        type: ModelType.TEXTAREA,
        label: T.common.email,
        path: 'channels.email.template'
      },
      {
        type: ModelType.CHECKBOX,
        label: T.common.sms,
        path: 'channels.sms.active'
      },
      {
        type: ModelType.TEXTAREA,
        label: T.common.sms,
        path: 'channels.sms.template'
      },
      {
        type: ModelType.OPTIONSV2,
        label: T.admin.notifications.alarmTriggers,
        path: 'trigger.alarmTrigger',
        options: alarmTriggerOptions,
        isRequired: true,
        hasError: _.isEmpty,
        properties: {
          isMultiple: true,
          isTypeahead: false,
          isClearable: true
        }
      }
    ];
  }, [T.admin.notifications.alarmTriggers, T.admin.notifications.broadcast, T.common.description, T.common.displayname, T.common.email, T.common.sms, T.common.subject, alarmTriggerOptions]);

  const updateNotification = useMutation({
    mutationFn: createMutationRequest(api.core.updateNotification),
    onMutate: (notification) => {
      const oldNotification = queryClient.getQueryData(notificationsKeys.notification(notificationId));
      queryClient.setQueryData(notificationsKeys.notification(notificationId), notification);
      return { oldNotification };
    },
    onSuccess: () => {
      queryClient.invalidateQueries(notificationsKeys.notification(notificationId));
    },
    onError: (error, unusedVariables, context) => {
      addErrorAlert(error.message);
      queryClient.setQueryData(notificationsKeys.notification(notificationId), context?.oldNotification);
    }
  });

  const createNotification = useMutation({
    mutationFn: createMutationRequest(api.core.createNotification),
    onSuccess: (notification) => {
      queryClient.invalidateQueries(notificationsKeys.notifications());
      queryClient.invalidateQueries(notificationsKeys.notification(notification.id));
      history.replace(`/admin/notifications/${notification.id}`);
    },
    onError: (error) => {
      addErrorAlert(error.message);
    }
  });

  const notification = useMemo(() => {
    return _.cloneDeep(editedNotification ?? serverState ?? defaultNotification);
  }, [serverState, editedNotification]);

  const onUpdateData = useCallback(() => {
    const notificationData = _.pick(notification, ['id', 'displayName', 'description', 'broadcast', 'channels', 'trigger']);
    updateNotification.mutate(notificationData);
  }, [notification, updateNotification]);
  const [isModelsOpen, , , toggleModels] = useBoolean(true);

  const onSave = useCallback(() => {
    if (notificationId) {
      onUpdateData();
    } else {
      showPublishDialog();
    }
  }, [notificationId, onUpdateData, showPublishDialog]);


  const onPublishToOrg = useCallback((notification, currentOrg) => {
    hidePublishDialog();
    createNotification.mutate({
      ...notification,
      organizationId: currentOrg.id
    });
  }, [createNotification, hidePublishDialog]);

  const saveDisabled = useMemo(() => {
    return (
      _.isEqual(notification, serverState) &&
      hasValidInput(notification, model)
    );
  }, [model, notification, serverState]);

  const contentToolbar = useMemo(() => {
    return (
      <Toolbar>
        <ToolbarContent alignment="alignLeft">
          <ToolbarGroup variant="button-group">
            <ToolbarItem>
              { updateNotification.isLoading && <Spinner size="lg" isSVG/> }
              <Button variant="primary" isDisabled={saveDisabled} onClick={onSave} isLoading={updateNotification.isLoading || createNotification.isLoading}>
                { T.common.save }
              </Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
    );
  }, [T.common.save, createNotification.isLoading, onSave, saveDisabled, updateNotification.isLoading]);

  return (
    <ContentPage breadcrumb={breadcrumbs} toolbar={contentToolbar} help="admin/notifications/edit">
      <Stack hasGutter>
        <StackItem>
          <Accordion isBordered asDefinitionList={false}>
            <AccordionItem>
              <AccordionToggle onClick={toggleModels} isExpanded={isModelsOpen}>
                { T.common.details }
              </AccordionToggle>
              <AccordionContent isHidden={!isModelsOpen}>
                <ModelsContainer
                  models={model}
                  data={notification}
                  isLoading={isLoading || isLoadingAlarms}
                  setData={setNotification}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </StackItem>
        { /* <StackItem >
          <Title headingLevel="h3">{ T.common.notificationSubscribers }</Title>
        </StackItem>
        <StackItem isFilled>
          <SimpleTable
            columns={columns}
            data={[]}
            isLoading={isLoading}
            sortDirection={defaultSortBy}
          />
        </StackItem> */ }
      </Stack>
      <ConfirmModalWithOrg
        // TODO: Translate
        title={'Publish to Organization'}
        icon={'info'}
        isOpen={isOpen}
        item={notification}
        confirmText={T.admin.common.publish}
        onConfirm={onPublishToOrg}
        onCancel={hidePublishDialog}
        template={'Connect this notfication to below organization.'}
        args={[notification?.displayName]}
        neededPrivilege={privileges.NotificationManagement}
      />
    </ContentPage>
  );
};

export default AdminNotificationEditPage;
